import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";

const ADMIN_FORM_DEFAULT = { username: "", full_name: "", password: "", phone: "", email: "" };

export default function ContractorsPage() {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "", active: true, service_charge_percent: 7, brand_name: "" });
  const [saving, setSaving] = useState(false);

  // Contractor Admin (read-only office login) management — one small dialog
  // per contractor listing/creating/removing that contractor's own
  // back-office logins.
  const [officeUsers, setOfficeUsers] = useState([]); // all role=contractor_admin users
  const [officeDialogFor, setOfficeDialogFor] = useState(null); // contractor obj
  const [officeForm, setOfficeForm] = useState(ADMIN_FORM_DEFAULT);
  const [officeSaving, setOfficeSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cs }, { data: users }] = await Promise.all([
        api.get("/contractors"),
        api.get("/users", { params: { role: "contractor_admin" } }),
      ]);
      setContractors(cs);
      setOfficeUsers(users || []);
    }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createOfficeUser = async () => {
    if (!officeForm.username.trim() || !officeForm.full_name.trim() || !officeForm.password) {
      return toast.error("Username, full name and password are required");
    }
    setOfficeSaving(true);
    try {
      await api.post("/users", {
        username: officeForm.username.trim().toLowerCase(),
        full_name: officeForm.full_name.trim(),
        password: officeForm.password,
        role: "contractor_admin",
        contractor_id: officeDialogFor.id,
        park_ids: [],
        phone: officeForm.phone || undefined,
        email: officeForm.email || undefined,
      });
      toast.success("Contractor Office login created");
      setOfficeForm(ADMIN_FORM_DEFAULT);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create login");
    } finally {
      setOfficeSaving(false);
    }
  };

  const removeOfficeUser = async (u) => {
    if (!window.confirm(`Remove Contractor Office login '${u.full_name}' (@${u.username})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    setSaving(true);
    try {
      const cleaned = { ...form };
      ["email", "phone", "contact_person", "brand_name"].forEach((k) => {
        if (typeof cleaned[k] === "string" && cleaned[k].trim() === "") delete cleaned[k];
      });
      const svc = Number(cleaned.service_charge_percent);
      cleaned.service_charge_percent = Number.isFinite(svc) ? svc : 7;
      if (editing) await api.put(`/contractors/${editing.id}`, cleaned);
      else await api.post("/contractors", cleaned);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join("; ")
        : detail || "Save failed";
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete contractor '${c.name}'?`)) return;
    try { await api.delete(`/contractors/${c.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <AppShell title="Contractors" subtitle="Agency master records. Supervisors are now assigned per (contractor, park) combo from the Employees tab." action={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button onClick={() => { setEditing(null); setForm({ name: "", contact_person: "", phone: "", email: "", active: true, service_charge_percent: 7, brand_name: "" }); setOpen(true); }} data-testid="contractor-add-button"><Plus className="mr-2 h-4 w-4" />Add agency</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit agency" : "New agency"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <FieldLite label="Name *" full><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="contractor-name-input" /></FieldLite>
            <FieldLite
              label="Brand Name (visible on Contractor Supervisor login)"
              full
            >
              <Input
                value={form.brand_name || ""}
                onChange={(e)=>setForm({...form,brand_name:e.target.value})}
                placeholder="e.g., Our Land Engineering Works"
                data-testid="contractor-brand-name-input"
              />
              <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
                Appears as <span className="font-medium">&ldquo;{(form.brand_name || form.name || "Contractor").trim()} Supervisor Login&rdquo;</span> on the public login screen. Leave blank to use the legal Name above.
              </p>
            </FieldLite>
            <FieldLite label="Contact person"><Input value={form.contact_person} onChange={(e)=>setForm({...form,contact_person:e.target.value})} /></FieldLite>
            <FieldLite label="Phone"><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} /></FieldLite>
            <FieldLite label="Email" full><Input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} /></FieldLite>
            <FieldLite label="Service Charge % (applied on Employer Cost)" full>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={form.service_charge_percent ?? 7}
                  onChange={(e)=>setForm({...form,service_charge_percent: e.target.value})}
                  className="pr-8"
                  data-testid="contractor-service-charge-input"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-500">%</span>
              </div>
            </FieldLite>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="contractor-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : contractors.length === 0 ? <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No contractors yet.</CardContent></Card>
        : <div className="grid gap-3 md:grid-cols-2">
            {contractors.map((c) => (
              <Card key={c.id} data-testid={`contractor-row-${c.id}`}>
                <CardContent className="flex items-start gap-3 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    {c.brand_name ? (
                      <div className="text-xs mt-0.5">
                        <span className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-medium text-blue-800" data-testid={`contractor-brand-${c.id}`}>
                          Login Brand: {c.brand_name}
                        </span>
                      </div>
                    ) : null}
                    <div className="text-xs text-muted-foreground mt-0.5">{c.contact_person || "—"} · {c.phone || "—"}</div>
                    <div className="text-xs text-muted-foreground">{c.email || "—"}</div>
                    <div className="text-xs mt-1">
                      <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800" data-testid={`contractor-service-charge-${c.id}`}>
                        Service charge: {Number(c.service_charge_percent ?? 7).toFixed(2)}%
                      </span>
                    </div>
                    <div className="mt-2 border-t pt-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                          Contractor Office logins
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[11px]"
                          onClick={() => { setOfficeDialogFor(c); setOfficeForm(ADMIN_FORM_DEFAULT); }}
                          data-testid={`contractor-${c.id}-add-office-login`}
                        >
                          <Plus className="mr-1 h-3 w-3" /> Add login
                        </Button>
                      </div>
                      {officeUsers.filter((u) => u.contractor_id === c.id).length === 0 ? (
                        <div className="mt-1 text-[11px] text-muted-foreground">None yet — read-only office view is unavailable until one is added.</div>
                      ) : (
                        <div className="mt-1 flex flex-col gap-1">
                          {officeUsers.filter((u) => u.contractor_id === c.id).map((u) => (
                            <div key={u.id} className="flex items-center justify-between gap-2 rounded border bg-muted/30 px-2 py-1 text-[11px]" data-testid={`contractor-office-login-${u.username}`}>
                              <span className="truncate"><span className="font-medium">{u.full_name}</span> <span className="text-muted-foreground">(@{u.username})</span></span>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeOfficeUser(u)} data-testid={`contractor-office-login-${u.username}-remove`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setForm({ name: c.name, contact_person: c.contact_person || "", phone: c.phone || "", email: c.email || "", active: c.active, service_charge_percent: c.service_charge_percent ?? 7, brand_name: c.brand_name || "" }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)}><Trash2 className="h-4 w-4" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
      }

      <Dialog open={!!officeDialogFor} onOpenChange={(v) => { if (!v) setOfficeDialogFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-4 w-4" /> New Contractor Office login — {officeDialogFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <FieldLite label="Username *"><Input value={officeForm.username} onChange={(e) => setOfficeForm({ ...officeForm, username: e.target.value })} data-testid="office-username-input" /></FieldLite>
            <FieldLite label="Full name *"><Input value={officeForm.full_name} onChange={(e) => setOfficeForm({ ...officeForm, full_name: e.target.value })} data-testid="office-fullname-input" /></FieldLite>
            <FieldLite label="Password *" full><PasswordInput value={officeForm.password} onChange={(e) => setOfficeForm({ ...officeForm, password: e.target.value })} data-testid="office-password-input" /></FieldLite>
            <FieldLite label="Phone"><Input value={officeForm.phone} onChange={(e) => setOfficeForm({ ...officeForm, phone: e.target.value })} /></FieldLite>
            <FieldLite label="Email"><Input value={officeForm.email} onChange={(e) => setOfficeForm({ ...officeForm, email: e.target.value })} /></FieldLite>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Read-only office login — can view this contractor's parks-visit status, work captures, workers-at-work,
            attendance calendar and monthly feedback, but cannot capture attendance or cleaning data.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfficeDialogFor(null)}>Cancel</Button>
            <Button onClick={createOfficeUser} disabled={officeSaving} data-testid="office-save-button">{officeSaving ? "Saving…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function FieldLite({ label, children, full }) {
  return (
    <div className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
